import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useKeyedState } from "@/hooks/useKeyedState";
import Link from "next/link";
import { useRouter } from "next/router";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { isTypingTarget } from "@/lib/isTypingTarget";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useSupportDrawerRedirect } from "@/hooks/useSupportDrawerState";
export function useSidebarGlobalBehaviors() {
  const $ = _c(7);
  const router = useRouter();
  const {
    activeGithubOrg,
    activeGithubOrgBlocked
  } = useActiveGithubOrgForUserNonSuspense();
  const urlOrg = router.query.org;
  const currentOrg = (typeof urlOrg === "string" ? urlOrg : undefined) || activeGithubOrg;
  const gPrefixActiveRef = useRef(false);
  const pendingSecondKeyRef = useRef(null);
  useSupportDrawerRedirect();
  let t0;
  let t1;
  if ($[0] !== currentOrg || $[1] !== router) {
    t0 = () => {
      const clearPendingSecondKey = () => {
        const pending = pendingSecondKeyRef.current;
        if (!pending) {
          return;
        }
        document.removeEventListener("keydown", pending.handler);
        clearTimeout(pending.timeoutId);
        pendingSecondKeyRef.current = null;
      };
      const handleKeyDown = event => {
        if (isTypingTarget(event.target)) {
          return;
        }
        if (event.key !== "g" || event.metaKey || event.ctrlKey || event.altKey) {
          return;
        }
        clearPendingSecondKey();
        gPrefixActiveRef.current = true;
        const shortcuts = {
          a: currentOrg ? `/${currentOrg}/analytics` : "/analytics",
          w: currentOrg ? `/${currentOrg}/runs/workflows` : "/runs/workflows",
          j: currentOrg ? `/${currentOrg}/runs/jobs` : "/runs/jobs",
          l: currentOrg ? `/${currentOrg}/logs` : "/logs",
          d: currentOrg ? `/${currentOrg}/docker-analytics` : "/docker-analytics",
          s: currentOrg ? `/${currentOrg}/stickydisks` : "/stickydisks",
          u: currentOrg ? `/${currentOrg}/usage` : "/usage",
          t: currentOrg ? `/${currentOrg}/settings` : "/settings",
          m: currentOrg ? `/${currentOrg}/monitors` : "/monitors"
        };
        const handleSecondKey = secondEvent => {
          if (secondEvent.key !== "g") {
            gPrefixActiveRef.current = false;
          }
          const isTyping = isTypingTarget(secondEvent.target);
          const hasModifier = secondEvent.metaKey || secondEvent.ctrlKey || secondEvent.altKey;
          const route = isTyping || hasModifier ? undefined : shortcuts[secondEvent.key];
          if (route) {
            secondEvent.stopImmediatePropagation();
            router.push(route);
          }
          clearPendingSecondKey();
        };
        document.addEventListener("keydown", handleSecondKey);
        const timeoutId = setTimeout(() => {
          gPrefixActiveRef.current = false;
          clearPendingSecondKey();
        }, 2000);
        pendingSecondKeyRef.current = {
          handler: handleSecondKey,
          timeoutId
        };
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        clearPendingSecondKey();
      };
    };
    t1 = [currentOrg, router];
    $[0] = currentOrg;
    $[1] = router;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== activeGithubOrgBlocked || $[5] !== currentOrg) {
    t2 = {
      activeGithubOrgBlocked,
      currentOrg,
      gPrefixActiveRef
    };
    $[4] = activeGithubOrgBlocked;
    $[5] = currentOrg;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function SidebarBlockedOrgBanner(t0) {
  const $ = _c(10);
  const {
    activeGithubOrgBlocked,
    currentOrg
  } = t0;
  const [bannerDismissed, setBannerDismissed] = useKeyedState(activeGithubOrgBlocked, false);
  if (!activeGithubOrgBlocked || bannerDismissed) {
    return null;
  }
  const t1 = currentOrg ? `/${currentOrg}/support` : "/support";
  let t2;
  if ($[0] !== t1) {
    t2 = <div className="flex-1 text-center">This account requires additional verification. <Link href={t1} className="cursor-pointer underline hover:no-underline">Contact support for help</Link></div>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== setBannerDismissed) {
    t3 = () => setBannerDismissed(true);
    $[2] = setBannerDismissed;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <X className="size-4" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3) {
    t5 = <Button onClick={t3} className="size-8 cursor-pointer rounded-md bg-white/10 p-0 transition-colors" variant="subtle" aria-label="Dismiss message">{t4}</Button>;
    $[5] = t3;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5) {
    t6 = <div className="fixed left-0 top-0 z-40 flex h-16 w-full items-center bg-red-500/80 text-sm text-white backdrop-blur-xs lg:text-base"><div className="flex w-full items-center justify-between px-4">{t2}{t5}</div></div>;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}